import { useState, useEffect } from 'react';
import { Trash2, Ticket, HomeIcon } from 'lucide-react';
import { serverURL } from '../helpers/dataServerURL';
import styles from '../css/ManualTicketsRemoval.module.css';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchAllOpenTickets,
  updateDailyActivityWithManualClose,
  deleteManuallyClosedTickets,
  postManuallyClosedTickets,
} from '../helpers/databaseAPI';

export default function ManualTicketsRemoval() {
  const [tickets, setTickets] = useState([]);
  // Creates a piece of state initialized as an empty object.
  // This state will be used to track which tickets are checked (selected) for removal.
  const [checked, setChecked] = useState({});

  const logInURL = serverURL.server.url;

  useEffect(() => {
    async function getOpenTkts() {
      const openTickets = await fetchAllOpenTickets(logInURL);
      setTickets(openTickets || []);
    }
    getOpenTkts();
  }, [logInURL]);

  useEffect(() => {
    console.log(checked, 'Checked list in use effect');
  });

  // Extracts all values from the object.
  // Boolean is being used as a filter function to keep only the truthy values
  //  (i.e., the tickets that are checked). It is equivalents to
  // .filter(value => value === true). It removes all falsy values
  // (false, 0, "", null, undefined, NaN).
  const checkedCount = Object.values(checked).filter(Boolean).length;
  // If tickets array is not empty and the number of checked tickets equals
  // to the total number of tickets, then allChecked is true, otherwise it is false.
  const allChecked = tickets.length > 0 && checkedCount === tickets.length;

  // This function toggles a single checkbox.
  // React passes the previous state of the checked object to the updater
  // function. It creates a new object that copies the previous state and
  // updates the value of the checkbox with the given id to its opposite value
  // (true becomes false, and false becomes true).
  // This ensures that the state is updated correctly without
  // mutating the previous state directly.
  // What if the object isn't the object? Suppose checked is empty
  // and the user clicks prev[105].
  // In that case, prev[105] is undefined, and !prev[105] will be true.
  const toggleOne = (id) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
    console.log(checked, 'New checked list');
  };

  //If all check boxes are checked, then if the if statement is true
  // and the setChecked function is called with an empty object,
  // which unchecks all checkboxes.
  // If not all checkboxes are checked, the else block is executed.
  // It creates a new object called next, where each ticket's id is
  // set to true, indicating that all checkboxes should be checked.

  // Why create a new object instead of modifying the existing
  // checked state directly?
  // In React, state should be treated as immutable.
  // Modifying the existing state directly can lead to unexpected
  // behavior and bugs.
  // By creating a new object, we ensure that React can properly detect
  // changes and re-render components as needed.
  // This approach also helps maintain a clear history of state changes,
  // which is important for debugging and understanding the flow of the
  // application.
  const toggleAll = () => {
    if (allChecked) {
      const next = {};
      tickets.forEach((t) => (next[t.Ticket] = false));
      setChecked(next);
    } else {
      const next = {};
      tickets.forEach((t) => (next[t.Ticket] = true));
      setChecked(next);
    }
  };

  const handleRemove = async () => {
    const selectedTickets = Object.keys(checked).filter((t) => checked[t] === true);
    await updateDailyActivityWithManualClose(logInURL, selectedTickets);
    await deleteManuallyClosedTickets(logInURL, selectedTickets);
    await postManuallyClosedTickets(logInURL, selectedTickets);
    setTickets((prev) => prev.filter((t) => !checked[t.Ticket]));
    setChecked({});
  };

  return (
    <div className={styles.topContainer}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Ticket className={styles.icon} />
            <div>
              <h1 className={styles.title}>Manual tickets removal</h1>
              <p className={styles.subtitle}>Select tickets to remove them from the active lot</p>
            </div>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.label}>Active {`Ticket${checkedCount > 1 ? 's' : ''}`}</div>
            <div className={styles.value}>{tickets.length}</div>
            <div className={styles.homeItem}>
              <HomeIcon className={styles.homeIcon} />
              <a href="/" className={styles.homeLink}>
                Home
              </a>
            </div>
          </div>
        </div>

        {/* Column headings */}

        <div className={styles.tableHeader}>
          <div>
            <input
              type="checkbox"
              checked={allChecked}
              onChange={toggleAll}
              aria-label="Select all tickets"
              className={styles.checkbox}
            />
          </div>
          <div>Ticket</div>
          <div>Check-in</div>
          <div>Section</div>
          <div>Lane</div>
          <div>Space</div>
        </div>

        {/* Rows */}
        <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {tickets.length === 0 && (
            <div className={styles.emptyState}>No tickets remaining. All tickets have been removed.</div>
          )}

          {tickets.map((t, idx) => {
            // The double exclamation mark converts any value into a boolean.
            // for example, !!true is true, !!false is false,!!1 is true,
            // !!0 is false, !!"hello" is true, !!"" is false, !!undefined is false, !!null is false
            const isChecked = !!checked[t.Ticket];
            return (
              <label
                key={t.id}
                htmlFor={`ticket-${t.Ticket}`}
                className={`${styles.ticketRow} ${isChecked ? styles.ticketRowChecked : ''}`}
              >
                <input
                  id={`ticket-${t.Ticket}`}
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => {
                    toggleOne(t.Ticket);
                    console.log('Ticket:', t.Ticket, 'isCHecked:', !isChecked, 'checked:', checked);
                  }}
                  className={styles.checkbox}
                />
                <div className={`${styles.text} ${isChecked ? styles.text2 : styles.text1}`}>{t.Ticket}</div>
                <div className={`${styles.text} ${isChecked ? styles.text2 : styles.text1}`}>
                  {new Date(t.TicketAdded).toLocaleDateString()}
                </div>
                <div className={`${styles.textSmall} ${isChecked ? styles.text3 : styles.text1}`}>
                  {t.SectionName}
                </div>
                <div className={`${styles.textSmall} ${isChecked ? styles.text3 : styles.text1}`}>{t.LaneNum}</div>
                <div className={`${styles.textSmall} ${isChecked ? styles.text3 : styles.text1}`}>
                  {t.SpaceNum}
                </div>
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.selectedCount}>
            {checkedCount > 0
              ? `${checkedCount} ticket${checkedCount > 1 ? 's' : ''} selected`
              : 'No tickets selected'}
          </div>
          <button
            onClick={handleRemove}
            disabled={checkedCount === 0}
            className={`${styles.button} ${checkedCount === 0 ? styles.disabledBtn : styles.buttonEnabled}`}
          >
            <Trash2 className={styles.icon} />
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}
